import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Users, ArrowRight, Check, Sparkles, Share2, Bell, Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';

const JointAccountTour = ({ open, onClose, onComplete }) => {
  const [currentStep, setCurrentStep] = useState(0);

  const steps = [
    {
      icon: Users,
      title: "Bienvenue dans le mode Partagé ! 👥",
      description: "Vous avez créé un compte joint. Découvrons ensemble comment le gérer efficacement avec votre partenaire.",
      color: "from-teal-500 to-cyan-600",
      tip: "Parfait pour couples, colocataires ou associés"
    },
    {
      icon: Share2,
      title: "Invitez votre partenaire",
      description: "Accédez aux paramètres du calendrier (icône ⚙️), puis dans l'onglet 'Membres', entrez l'email de la personne à inviter.",
      color: "from-purple-500 to-indigo-600",
      tip: "La personne doit avoir un compte sur l'application"
    },
    {
      icon: Bell,
      title: "Restez synchronisés",
      description: "Toutes les opérations ajoutées par l'un ou l'autre sont visibles en temps réel. Le calendrier se met à jour automatiquement.",
      color: "from-amber-500 to-orange-600",
      tip: "Rafraîchissement automatique toutes les 15 secondes"
    },
    {
      icon: Shield,
      title: "Chacun son rôle",
      description: "Le créateur du calendrier est le 'propriétaire' (👑). Les deux membres peuvent ajouter et modifier les opérations.",
      color: "from-emerald-500 to-green-600",
      tip: "Seul le propriétaire peut inviter ou supprimer des membres"
    },
    {
      icon: Sparkles,
      title: "Vous êtes prêts ! ✨",
      description: "Commencez à gérer vos finances ensemble. Ajoutez vos revenus communs, vos dépenses partagées et vos objectifs d'épargne.",
      color: "from-rose-500 to-pink-600",
      tip: "Conseil : créez un objectif d'épargne commun pour vos projets"
    }
  ];

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      onComplete?.();
      onClose?.();
    }
  };

  const handleSkip = () => {
    onComplete?.();
    onClose?.();
  };

  useEffect(() => {
    if (open) {
      setCurrentStep(0);
    }
  }, [open]);

  if (!open) return null;

  const step = steps[currentStep];
  const StepIcon = step.icon;
  const isLastStep = currentStep === steps.length - 1;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      >
        <motion.div
          initial={{ scale: 0.9, y: 20 }}
          animate={{ scale: 1, y: 0 }}
          exit={{ scale: 0.9, y: 20 }}
          className="bg-white rounded-3xl w-full max-w-md overflow-hidden shadow-2xl"
        >
          {/* Header with gradient */}
          <div className={`bg-gradient-to-br ${step.color} p-6 text-white relative overflow-hidden`}>
            {/* Background decoration */}
            <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
            <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/10 rounded-full translate-y-1/2 -translate-x-1/2" />
            
            {/* Icon */}
            <motion.div
              key={currentStep}
              initial={{ scale: 0, rotate: -180 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ type: 'spring', stiffness: 200 }}
              className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mb-4 relative z-10"
            >
              <StepIcon className="h-8 w-8" />
            </motion.div>
            
            {/* Title */}
            <motion.h2
              key={`title-${currentStep}`}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="text-xl font-bold relative z-10"
            >
              {step.title}
            </motion.h2>
          </div>

          {/* Content */}
          <div className="p-6">
            <motion.p
              key={`desc-${currentStep}`}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-gray-600 leading-relaxed mb-4"
            >
              {step.description}
            </motion.p>

            {/* Tip box */}
            <motion.div
              key={`tip-${currentStep}`}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-gray-50 rounded-xl p-3 mb-6"
            >
              <p className="text-sm text-gray-500">
                💡 <span className="font-medium text-gray-700">{step.tip}</span>
              </p>
            </motion.div>

            {/* Progress dots */}
            <div className="flex justify-center gap-2 mb-6">
              {steps.map((_, index) => (
                <motion.div
                  key={index}
                  className={`h-2 rounded-full transition-all ${
                    index === currentStep 
                      ? 'w-6 bg-teal-500' 
                      : index < currentStep 
                        ? 'w-2 bg-teal-300' 
                        : 'w-2 bg-gray-200'
                  }`}
                  animate={{ 
                    scale: index === currentStep ? 1.1 : 1 
                  }}
                />
              ))}
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              {!isLastStep && (
                <Button
                  variant="ghost"
                  onClick={handleSkip}
                  className="flex-1 text-gray-500"
                >
                  Passer
                </Button>
              )}
              <Button
                onClick={handleNext}
                className={`flex-1 bg-gradient-to-r ${step.color} hover:opacity-90 text-white gap-2`}
              >
                {isLastStep ? (
                  <>
                    <Check className="h-4 w-4" />
                    C'est parti !
                  </>
                ) : (
                  <>
                    Suivant
                    <ArrowRight className="h-4 w-4" />
                  </>
                )}
              </Button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default JointAccountTour;
